import { useState } from "react";
import { createPortal } from "react-dom";
import { RiCloseLine, RiArrowRightSLine, RiAddLine } from "@remixicon/react";

const SERVICE_CATEGORIES = [
  { name: "Transportation", description: "Chauffeurs, armored cars, ramp access (tarmac pickup), helicopter shuttles, and yacht tenders." },
  { name: "Catering", description: "Standard catering, restaurant procurement, and special dietary requests." },
  { name: "Beverage & Bar", description: "Standard stock, specific vintages, sommelier services, and specialized soft drinks." },
  { name: "Passenger Services", description: "Air nannies, pet logistics, toys/entertainment, and pet entry paperwork." },
  { name: "Wellness & Medical", description: "Spa kits, massage therapists, medical escorts, and oxygen." },
  { name: "Security", description: "Bodyguards, secure drivers, and bug sweeping." },
  { name: "Concierge & Documents", description: "Visas, customs expediting, event tickets, and hotel/villa booking." },
  { name: "Trip Protection", description: "De-icing waivers, trip cancellation insurance, and carbon offsetting." },
];

const AddServiceModal = ({ onSelect, onClose, editingService = null }) => {
  const [selectedCategory, setSelectedCategory] = useState(editingService?.name || null);
  const [details, setDetails] = useState(editingService?.details || "");

  const handleAdd = () => {
    if (!selectedCategory || !details.trim()) return;
    onSelect(selectedCategory, details.trim());
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-xl mx-4 mb-0 sm:mb-0">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{editingService ? "Edit Service" : "Add Service"}</h2>
            {selectedCategory && (
              <p className="text-sm text-gray-500">{selectedCategory}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        {!selectedCategory ? (
          <div className="p-4 grid grid-cols-2 gap-3">
            {SERVICE_CATEGORIES.map((cat) => (
              <button
                key={cat.name}
                onClick={() => setSelectedCategory(cat.name)}
                className="text-left border border-gray-200 rounded-xl p-3.5 hover:border-gray-300 hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-start justify-between gap-1">
                  <h3 className="text-sm font-semibold text-gray-900 leading-tight">{cat.name}</h3>
                  <RiArrowRightSLine className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0 mt-0.5" />
                </div>
                <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{cat.description}</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-1.5">
                Service Details <span className="text-red-500">*</span>
              </label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Add specific details"
                rows={4}
                className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-primary-400 transition-colors resize-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setSelectedCategory(null); setDetails(""); }}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-gray-50 border border-gray-200 text-gray-800 text-sm font-semibold rounded-full hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleAdd}
                disabled={!details.trim()}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 border text-sm font-semibold rounded-full transition-colors ${details.trim() ? "bg-primary-500 border-primary-500 text-white hover:bg-primary-600" : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"}`}
              >
                <RiAddLine className="w-4 h-4" />
                {editingService ? "Save Changes" : "Add Service"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AddServiceModal;
